import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ChevronRight, FileText, Loader2, Plus, Search } from 'lucide-react';
import { useClientNames } from '@/api/clients';
import { useTxPlans, type TxPlanStatus } from '@/api/txPlans';
import { useAuthStore } from '@/stores/auth';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { StatusBadge } from '@/components/ui/Badge';
import { Input } from '@/components/ui/FormField';

import { extractErrorMessage } from '@/lib/extractErrorMessage';
const STATUS_FILTERS: Array<{ id: TxPlanStatus | 'All'; label: string }> = [
    { id: 'All',      label: 'All' },
    { id: 'Active',   label: 'Active' },
    { id: 'Signed',   label: 'Signed' },
    { id: 'Draft',    label: 'Draft' },
    { id: 'Archived', label: 'Archived' },
];

function formatDate(iso: string | null): string {
    if (!iso) return '—';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

export function PlansPanel() {
    const canEdit = useAuthStore((s) => s.hasPermission('edit_treatment_plans'));
    const navigate = useNavigate();
    const { data: clientList } = useClientNames();
    const [clientId, setClientId] = useState<string>('');
    const [statusFilter, setStatusFilter] = useState<TxPlanStatus | 'All'>('Active');
    const [search, setSearch] = useState('');

    const listOpts = clientId
        ? (statusFilter === 'All'
            ? { clientId, includeArchived: true }
            : { clientId, status: statusFilter })
        : { clientId: '' };
    const { data, isLoading, isError, error } = useTxPlans(listOpts);

    const clientOptions = useMemo(
        () => (clientList ?? [])
            .filter((c) => c.status !== 'Inactive')
            .sort((a, b) => (a.last + a.first).localeCompare(b.last + b.first)),
        [clientList],
    );

    const rows = useMemo(() => {
        if (!data) return [];
        const q = search.trim().toLowerCase();
        if (!q) return data;
        return data.filter((p) => p.title.toLowerCase().includes(q));
    }, [data, search]);

    return (
        <div>
            <div className="flex items-center justify-between mb-1">
                <p className="text-xs text-text-subtle">
                    Client treatment plans bundle active behaviors and programs for review
                    and signature. Plans reference the live operational definitions —
                    editing a behavior or program updates every plan that references it.
                </p>
                {canEdit && clientId && (
                    <Button
                        variant="primary"
                        size="sm"
                        asChild
                    >
                        <Link to={`/treatment-plans/plans/new?client=${clientId}`}>
                            <Plus className="w-3.5 h-3.5" /> New plan
                        </Link>
                    </Button>
                )}
            </div>

            <Card className="p-0 overflow-hidden mt-3">
                <div className="flex flex-wrap items-center gap-3 p-3 border-b border-border">
                    <label className="flex items-center gap-2 text-xs text-text-muted">
                        Client
                        <select
                            value={clientId}
                            onChange={(e) => setClientId(e.target.value)}
                            aria-label="Select client"
                            className="h-8 px-2 text-xs bg-background border border-border rounded-md text-text focus:border-accent focus:outline-none min-w-[12rem]"
                        >
                            <option value="">— choose a client —</option>
                            {clientOptions.map((c) => (
                                <option key={c.id} value={c.id}>
                                    {c.last}, {c.first}
                                </option>
                            ))}
                        </select>
                    </label>
                    {clientId && (
                        <div className="relative flex-1 min-w-[180px] max-w-xs">
                            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-text-subtle" />
                            <Input
                                type="search"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                placeholder="Search by title…"
                                aria-label="Search plans by title"
                                className="pl-8 h-8 py-1"
                            />
                        </div>
                    )}
                </div>

                {clientId && (
                    <div className="flex items-center gap-1 p-2 border-b border-border">
                        {STATUS_FILTERS.map((s) => (
                            <button
                                key={s.id}
                                type="button"
                                onClick={() => setStatusFilter(s.id)}
                                className={`px-2.5 py-1 text-xs font-medium rounded-md transition-colors ${
                                    statusFilter === s.id
                                        ? 'bg-surface-muted text-text'
                                        : 'text-text-muted hover:text-text hover:bg-surface-muted'
                                }`}
                                aria-pressed={statusFilter === s.id}
                            >
                                {s.label}
                            </button>
                        ))}
                        <div className="ml-auto text-xs text-text-subtle tabular-nums">
                            {rows.length} {rows.length === 1 ? 'plan' : 'plans'}
                        </div>
                    </div>
                )}

                {!clientId ? (
                    <div className="p-12 text-center text-text-subtle">
                        <FileText className="w-6 h-6 mx-auto mb-2 opacity-40" />
                        <p>Pick a client to view their treatment plans.</p>
                    </div>
                ) : isLoading ? (
                    <div className="p-12 flex items-center justify-center text-text-subtle">
                        <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading…
                    </div>
                ) : isError ? (
                    <div className="p-12 text-center text-error">
                        <p className="font-medium mb-1">Couldn't load plans</p>
                        <p className="text-xs text-text-subtle">
                            {extractErrorMessage(error, 'Please try again.')}
                        </p>
                    </div>
                ) : rows.length === 0 ? (
                    <div className="p-12 text-center text-text-subtle">
                        <p>No plans in this view.</p>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="text-left text-2xs uppercase tracking-wider text-text-subtle border-b border-border">
                                    <th className="px-3 py-2 font-bold">Title</th>
                                    <th className="px-3 py-2 font-bold">Status</th>
                                    <th className="px-3 py-2 font-bold">BCBA</th>
                                    <th className="px-3 py-2 font-bold">Start</th>
                                    <th className="px-3 py-2 font-bold">Review</th>
                                    <th className="px-3 py-2 font-bold">Signed</th>
                                    <th className="px-3 py-2 w-6" aria-hidden />
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((p) => (
                                    <tr
                                        key={p.id}
                                        onClick={() => navigate(`/treatment-plans/plans/${p.id}`)}
                                        className="border-b border-border last:border-b-0 hover:bg-surface-muted group cursor-pointer"
                                    >
                                        <td className="px-3 py-2 font-medium text-text">{p.title}</td>
                                        <td className="px-3 py-2"><StatusBadge status={p.status} /></td>
                                        <td className="px-3 py-2 text-text-muted whitespace-nowrap">{p.bcbaName || '—'}</td>
                                        <td className="px-3 py-2 text-text-muted whitespace-nowrap">{formatDate(p.startDate)}</td>
                                        <td className="px-3 py-2 text-text-muted whitespace-nowrap">{formatDate(p.reviewDate)}</td>
                                        <td className="px-3 py-2 text-text-muted whitespace-nowrap">{formatDate(p.signedAt)}</td>
                                        <td className="px-2 py-2 text-text-subtle">
                                            <ChevronRight className="w-4 h-4 opacity-60 group-hover:opacity-100 transition-opacity" />
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </Card>
        </div>
    );
}
